/* eslint-disable no-return-assign */
import * as Sentry from "@sentry/vue";
import { apiV1, apiV2 } from "@/store";
import axios from "axios";
import { responseIsInvalidPromotion } from "../../helpers/move-in-cost/response-is-invalid-promotion";

function selectCurrentUnitOrFirstAvailable(availableUnits, currentUnitId) {
  // @todo sitelink_id - PMS Heresy
  const currentlySelectedUnit = availableUnits.find(unit => String(unit.sitelink_id) === String(currentUnitId));
  return currentlySelectedUnit || availableUnits[0];
}

const state = {
  allFacilities: [],
  availableUnits: [],
  facility: null,
  facilityLoading: false,
  facilityAlerts: [],
  facilityMerchandise: [],
  facilityList: [],
  facilityListPagination: {
    total: null
  },
  facilityPromotions: [],
  loading: [],
  merchandiseCart: [],
  moveInCost: null,
  moveInCostEndDate: null,
  moveInCostLoading: false,
  moveInInstructions: null,
  promotionOptions: null,
  quotedRate: null,
  requestUhaulLoading: false,
  unit: null,
  unitFeatures: null,
  isPromoDeferred: false // Sent with move-in-cost request to tell PMS to apply promo to next month
};

const actions = {

  getEveryFacility: ({ commit }) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`/facilities?per_page=999&page=1`)
        .then((resp) => {
          commit("setAllFacilities", resp.data.facilities.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getFacilityList: ({ commit, dispatch, state }, params) => {
    commit("addToLoadingState", "Loading Facilities");
    return apiV1.get(
      `facilities`,
      {
        params: {
          per_page: params.itemsPerPage,
          page: params.page,
          order: params.sortDesc[0] ? "desc" : "asc",
          sort: params.sortBy[0]
        }
      }
    ).then((resp) => {
      commit(
        "setFacilityList",
        resp.data ? resp.data.facilities.data : []
      );
      commit("setFacilityListPagination", { total: resp.data.facilities.total });
      commit("removeFromLoadingState", "Loading Facilities");
    }).catch((err) => {
      dispatch("handleError", err, { root: true });
      commit("removeFromLoadingState", "Loading Facilities");
    });
  },

  getFacility: ({ commit }, id) => {
    commit("facilityLoading");
    return new Promise((resolve, reject) => {
      apiV1.get(`/facilities/${id}`)
        .then((resp) => {
          localStorage.setItem("locationCode", resp.data.facility.location_code);
          localStorage.setItem("facilityId", resp.data.facility.id);
          commit("facilityLoaded");
          commit("setFacility", resp.data.facility);
          resolve(resp);
        })
        .catch((err) => {
          commit("facilityLoaded");
          reject(err);
        });
    });
  },

  getFacilityAlerts: ({ commit }, id) => {
    if (id === undefined || id === null) {
      return Promise.reject(new Error("Can't get facility alerts because id is null or undefined"));
    }
    return new Promise((resolve, reject) => {
      apiV1.get(`/facilities/${id}/alerts`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getFacilityByLocation: ({ commit }, facilityLocation) => {
    commit("addToLoadingState", "Loading Facilities");
    return apiV1.get(`/facilities/search`, {
      params: {
        location: facilityLocation
      }
    }).then((resp) => {
      if (resp.data && resp.data.facilities) {
        commit("setFacilityList", resp.data.facilities);
      }
    }).catch((err) => {
      commit("errorMessage", err, { root: true });
    }).finally(() => {
      commit("removeFromLoadingState", "Loading Facilities");
    });
  },

  getUnitFeatures: ({ commit }, payload) => {
    commit("invalidPromotion", false);
    // Sometimes if unit_id comes from unit_id_first_available, it's a CSV...
    // So we grab the first value.
    const unitId = `${payload.unit_id}`.split(",")[0];
    return new Promise((resolve, reject) => {
      apiV2.get(`/facilities/${payload.facilityId}/units/${unitId}`)
        .then((resp) => {
          commit("setUnitFeatures", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getConvenienceFee: ({}, facilityId) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`facilities/${facilityId}/convenience-fees`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getPaymentMethods: ({}, facilityId) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`facilities/${facilityId}/payment-methods`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getMoveInCost: ({ commit, rootState, getters }, additionalPayload) => {
    // All properties must be present in the post object, or the request will fail:
    let promotionId = additionalPayload.external_promotion_id || rootState.tenant.options.selectedPromotion || 0;
    promotionId = `${promotionId}`.replace("-defer", "");
    const payload = {
      facilityId: additionalPayload.facilityId,
      external_fee_ids: additionalPayload.external_fee_ids || null,
      external_merchandise: additionalPayload.external_merchandise || getters.merchandisePayload,
      external_unit_id: additionalPayload.external_unit_id || getters.unitId,
      external_promotion_id: promotionId,
      move_in_date: rootState.tenant.options.moveInDate,
      external_tpp_id: rootState.tenant.options.tenantProtection.external_id || null,
      external_reservation_id: additionalPayload.external_reservation_id || rootState.tenant.info.waiting_id || null,
      is_promo_deferred: rootState.facilities.isPromoDeferred
    };
    Object.keys(payload).forEach(key => {
      if (typeof payload[key] === "number") {
        payload[key] = payload[key].toString();
      }
    });

    if (additionalPayload.move_in_date !== undefined && additionalPayload.move_in_date !== null) {
      payload.move_in_date = additionalPayload.move_in_date;
    } else {
      payload.move_in_date = rootState.tenant.options.moveInDate;
    }

    commit("moveInCostLoading", true);
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/move-in-cost`, payload)
        .then((resp) => {
          commit("setMoveInCost", resp.data);
          commit("moveInCostLoading", false);
          resolve(resp);
        })
        .catch((err) => {
          commit("moveInCostLoading", false);
          Sentry.captureException(err.response);
          if (responseIsInvalidPromotion(err)) {
            commit("invalidPromotion", true);
            commit("updatePromotion", null, { root: true });
          }
          reject(err);
        });
    });
  },

  getTenantProtectionPlans: ({}, facilityId) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${facilityId}/tenant-protection-plans`, {
        params: {
          select: [
            "external_id",
            "coverage",
            "premium"
          ]
        }
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getPromotionOptions: ({ commit }, payload) => {
    // for backward compatibility:
    if (payload && payload.concession_id && !payload.externalPromotionId) {
      payload.externalPromotionId = payload.concession_id;
    }
    if (!payload.externalPromotionId) payload.externalPromotionId = null;
    commit("addToLoadingState", "Promotion options");
    commit("setPromotionOptions", null);
    return apiV2.get(`facilities/${payload.facilityId}/concession-options?base_concession=${payload.externalPromotionId}`)
      .then((resp) => {
        if (!resp.data) {
          throw Error("No response");
        }
        commit("setPromotionOptions", resp.data);
      })
      .catch((err) => {
        commit("errorMessage", err, { root: true });
      })
      .finally(() => {
        commit("removeFromLoadingState", "Promotion options");
      });
  },

  getFacilityPromotions: ({ commit }, payload) => {
    if (!payload || !payload.facilityId) return null;
    commit("addToLoadingState", "Facility Promotions");
    commit("setPromotionOptions", null);
    const params = {};
    if (payload.realtime) {
      params.realtime = 1;
    }
    return apiV2.get(
      `facilities/${payload.facilityId}/promotions`,
      { params }
    ).then((resp) => {
      if (!resp.data) {
        throw Error("No promotion data");
      }
      commit("setFacilityPromotions", resp.data);
    })
      .catch((err) => {
        commit("errorMessage", err, { root: true });
      }).finally(() => {
        commit("removeFromLoadingState", "Facility Promotions");
      });
  },

  getMerchandise: ({ commit, dispatch }, facilityId) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${facilityId}/merchandise`, {
        params: {
          select: [
            "name",
            "external_id",
            "price",
            "tax_rate_1",
            "tax_rate_2"
          ]
        }
      })
        .then((resp) => {
          const merchandise = resp.data.map(merchandise => {
            return {
              ...merchandise,
              quantity: 0
            };
          });
          commit("setMerchandise", merchandise);
          resolve(resp);
        })
        .catch(() => {
          dispatch("handleError", "Error retrieving merchandise", { root: true });
        });
    });
  },

  getSimilarUnits: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/units/${payload.unit_id}/similar`)
        .then((resp) => {
          commit("setAvailableUnits", resp.data);
          const unit = selectCurrentUnitOrFirstAvailable(resp.data, payload.unit_id);
          if (unit) {
            commit("setUnit", unit);
          }
          resolve(resp);
        })
        .catch(() => {
          dispatch("handleError", "Error retrieving similar units", { root: true });
        });
    });
  },

  getMoveInInstructions: ({ commit, dispatch }, facility_id) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/facilities/${facility_id}/move-in-instructions`)
        .then((resp) => {
          commit("setMoveInInstructions", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          // 404 is currently an expected response - do not throw exception
          if (err.response.status === 404) {
            commit("setMoveInInstructions", {
              facility_id: facility_id,
              instructions: "No move-in instructions exist for this facility"
            });
          } else {
            dispatch("handleError", "Error retrieving move-in instructions", { root: true });
          }
        });
    });
  },

  sendEmail: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`facilities/${payload.facilityId}/email-info`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  requestUhaul: ({ commit }, payload) => {
    commit("requestUhaulLoading", true);
    apiV1.post(`facilities/${payload.facilityId}/request-uhaul`, payload)
      .then(() => {
        commit("requestUhaulLoading", false);
      }).catch(() => {
        commit("requestUhaulLoading", false);
      });
  },

  submitNote: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.patch(`/facilities/${payload.id}/notes`, { content: payload.content })
        .then((resp) => {
          dispatch("handleSuccess", "Facility notes successfully updated!");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  moveIn: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/move-in`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  speederent: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/speederent`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  merchandisePayment: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/merchandise/payment`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  emailMerchandiseReceipt: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/email-merchandise-receipt`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  sendMoveInNotification: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`facilities/${payload.facilityId}/move-in/send-notification`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  createMoveInInstructions: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`/move-in-instructions/`, payload)
        .then((resp) => {
          commit("setMoveInInstructions", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getIsBlacklisted: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      const payloadPhone = payload.phone.replace(/[^\d]/g, "");
      const payloadIdNumber = payload.idNumer.replace(/[^\d]/g, "");
      axios.create({
        baseURL: process.env.VUE_APP_API_ALT_URL
      })
        .get(
          `/blacklist-customers/?phone_number=${payloadPhone}&email_address=${payload.email}&drivers_license_number=${payloadIdNumber}`
        )
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  updateMoveInInstructions: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.put(`/move-in-instructions/${payload.id}`, payload)
        .then((resp) => {
          commit("setMoveInInstructions", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  clearMoveInInstructions: ({ commit }) => {
    commit("setMoveInInstructions", null);
  },

  handleQuotedRate: ({ commit, dispatch }, payload) => {
    if (payload.rate || payload.rate === 0) {
      commit("setQuotedRate", payload.rate);
    } else if (payload.quoted_price || payload.quoted_price === 0) {
      commit("setQuotedRate", payload.quoted_price);
    } else {
      let unitId = payload.unit_id_first_available;
      dispatch("handleError", `No quoted rate found for unit ${unitId}`);
    }
  }
};

const mutations = {
  addToLoadingState(state, eventName) {
    state.loading.push(eventName);
  },

  removeFromLoadingState(state, eventName) {
    state.loading = state.loading.filter(eachEventName => eachEventName !== eventName);
  },

  setFacilityList: (state, facilities) => {
    state.facilityList = facilities;
  },

  setFacilityListPagination: (state, facilityListPagination) => {
    state.facilityListPagination = facilityListPagination;
  },

  setFacilityPromotions: (state, promotions) => {
    state.facilityPromotions = promotions;
  },

  facilityLoading: (state) => {
    state.facilityLoading = true;
  },

  facilityLoaded: (state) => {
    state.facilityLoading = false;
  },

  setAllFacilities: (state, facilities) => {
    state.allFacilities = facilities;
  },

  setFacility: (state, facility) => {
    state.facility = facility;
  },

  commitAlerts: (state, alerts) => {
    state.facilityAlerts = alerts;
  },

  setMerchandise: (state, facilityMerchandise) => {
    state.facilityMerchandise = facilityMerchandise;
  },

  updateMerchandiseCart: (state, merchandiseCart) => {
    state.merchandiseCart = merchandiseCart;
  },

  setAvailableUnits: (state, availableUnits) => {
    state.availableUnits = availableUnits;
  },

  setUnit: (state, unit) => {
    state.unit = unit;
  },

  setUnitFeatures: (state, unitFeatures) => {
    state.unitFeatures = unitFeatures;
  },

  setQuotedRate: (state, rate) => {
    state.quotedRate = rate;
  },

  setMoveInCost: (state, moveInCost) => {
    let value = null;
    if (moveInCost) {
      value = Object.assign({}, moveInCost);
      value.fees.push({
        name: "First Monthly Rent Fee",
        cost: moveInCost.rent
      });
      if (moveInCost.tpp_name.length) {
        value.fees.push({
          name: moveInCost.tpp_name.replace("Insurance", "TPP"),
          cost: moveInCost.tpp
        });
      }
      value.fees = value.fees.map(fee => {
        fee.name = fee.name.replace("Insurance", "TPP");
        return fee;
      });
      state.moveInCostEndDate = moveInCost.rent_end_date;
    }
    state.moveInCost = value;
  },

  setDeferPromotion: (state, val) => {
    state.isPromoDeferred = val;
  },

  setMoveInInstructions: (state, moveInInstructions) => {
    state.moveInInstructions = moveInInstructions;
  },

  moveInCostLoading: (state, loadState) => {
    state.moveInCostLoading = loadState;
  },

  requestUhaulLoading(state, loading) {
    state.requestUhaulLoading = loading;
  },

  setPromotionOptions(state, data) {
    if (data) {
      data.next_month.id = `${data.next_month.id}-defer`;
    }
    state.promotionOptions = data;
  }
};

const getters = {
  merchandisePayload: (state, getters, rootState) => {
    if (!rootState.facilities.merchandiseCart) {
      return null;
    }
    return rootState.facilities.merchandiseCart.map(merch => ({
      external_id: String(merch.external_id),
      quantity: parseInt(merch.quantity)
    }));
  },
  isLoading: (state) => (eventName) => {
    return state.loading.includes(eventName);
  },
  promotionOptionsLoading: (state, getters) => {
    return getters.isLoading("Promotion options");
  },
  defaultPromotionOption: (state) => {
    if (!state.promotionOptions) return null;
    const regEx = /^\s*no\s*promotion\s*$/i;
    if (
      state.promotionOptions.this_month &&
      state.promotionOptions.this_month.text &&
      !regEx.test(state.promotionOptions.this_month.text)
    ) {
      return state.promotionOptions.this_month;
    }
    if (
      state.promotionOptions.next_month &&
      state.promotionOptions.next_month.text &&
      !regEx.test(state.promotionOptions.next_month.text)
    ) {
      return state.promotionOptions.next_month;
    }
    return null;
  },
  facilityPromotionsLoading: (state, getters) => {
    return getters.isLoading("Facility Promotions");
  },
  facilityList(state) {
    return state.facilityList;
  },
  facilityListPagination(state) {
    return state.facilityListPagination;
  },
  merchTaxTotal: (state) => {
    return state.moveInCost && state.moveInCost.merchandise_tax ? parseFloat(state.moveInCost.merchandise_tax) : 0.00;
  },
  merchCartSubtotal: (state) => {
    return state.moveInCost && state.moveInCost.merchandise ? parseFloat(state.moveInCost.merchandise) : 0.00;
  },
  unitId: (state) => {
    if (state.unit && state.unit.unit_id) {
      return state.unit.unit_id;
    } else if (state.unit && state.unit.unit_id_first_available) {
      return state.unit.unit_id_first_available;
    } else if (state.unit && state.unit.UnitID_PriceRef) {
      return state.unit.UnitID_PriceRef;
    } else if (state.unit && state.unit.sitelink_id) {
      return state.unit.sitelink_id;
    } else {
      return undefined;
    }
  }
};

export default {
  state,
  actions,
  mutations,
  getters
};
